import apiService from './ApiService';

const ThreadsService = {
  /**
   * Obtiene los mensajes de threads de un asistente con paginación
   * @param {string} idAsistente - ID del asistente
   * @param {string} filtroRol - Rol a filtrar ('user' para mensajes de alumnos)
   * @param {number} pagina - Página actual (default: 1)
   * @param {number} tamañoPagina - Threads por página (default: 50, max: 200)
   * @param {number} limiteMensajesPorThread - Mensajes por thread (default: 100, max: 500)
   * @returns {Promise} Promesa que resuelve con la respuesta de la API
   */
  async getThreadsMessages(
    idAsistente, 
    filtroRol = 'user', 
    pagina = 1, 
    tamañoPagina = 300, 
    limiteMensajesPorThread = 100
  ) {
    try {
      
      const requestBody = {
        IdAsistente: idAsistente,
        FiltroRol: filtroRol,
        Pagina: pagina,
        TamañoPagina: Math.min(tamañoPagina, 200), // Máximo 200
        LimiteMensajesPorThread: Math.min(limiteMensajesPorThread, 500) // Máximo 500
      };
      
      
      const response = await apiService.post('/Asistente/threads-messages', requestBody);
      
      
      // Validar estructura de respuesta
      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los mensajes');
      }
      
      // Procesar la nueva estructura de respuesta con threads
      if (response.threads && Array.isArray(response.threads)) {
        // Aplanar los mensajes de todos los threads para la tabla
        const mensajesAplanados = [];
        
        response.threads.forEach(thread => {
          if (thread.mensajes && Array.isArray(thread.mensajes)) {
            const mensajesOrdenados = [...thread.mensajes].sort((a, b) => {
              const aFecha = a.fechaEnvio ? new Date(a.fechaEnvio).getTime() : 0;
              const bFecha = b.fechaEnvio ? new Date(b.fechaEnvio).getTime() : 0;
              return aFecha - bFecha;
            });

            mensajesOrdenados.forEach((mensaje, index) => {
              let mensajeRecibido = '';
              if (mensaje.rol === 'user') {
                for (let i = index + 1; i < mensajesOrdenados.length; i += 1) {
                  if (mensajesOrdenados[i].rol === 'assistant') {
                    mensajeRecibido = mensajesOrdenados[i].contenido || '';
                    break;
                  }
                }
              }

              mensajesAplanados.push({
                ...mensaje,
                threadId: thread.idThread,
                fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
                fechaEnviado: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                fecha: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                mensaje: mensaje.contenido || '',
                mensajeRecibido,
                usuario: mensaje.rol === 'user' ? 'Estudiante' : 'Asistente',
                rol: mensaje.rol
              });
            });
          }
        });
        
        return {
          mensajes: mensajesAplanados,
          threads: response.threads,
          totalThreads: response.totalThreads || 0,
          totalMessages: response.totalMessages || 0,
          diaMasActivo: response.diaMasActivo || response.DiaMasActivo || null,
          mensajesDiaMasActivo: response.mensajesDiaMasActivo ?? response.MensajesDiaMasActivo ?? 0,
          horaPico: response.horaPico ?? response.HoraPico ?? null,
          mensajesHoraPico: response.mensajesHoraPico ?? response.MensajesHoraPico ?? 0,
          promedioMensajesPorDiaActivo: response.promedioMensajesPorDiaActivo ?? response.PromedioMensajesPorDiaActivo ?? 0,
          diasConActividad: response.diasConActividad ?? response.DiasConActividad ?? 0,
          ultimaActividad: response.ultimaActividad || response.UltimaActividad || null,
          paginaActual: response.paginaActual || pagina,
          tamañoPagina: response.tamañoPagina || tamañoPagina,
          totalThreadsEnBD: response.totalThreadsEnBD || 0,
          totalPaginas: response.totalPaginas || 1,
          tienePaginaSiguiente: response.tienePaginaSiguiente || false,
          tienePaginaAnterior: response.tienePaginaAnterior || false,
          total: response.totalMessages || 0,
          ok: response.ok,
          statusCode: response.statusCode
        };
      }
      
      // Fallback para retrocompatibilidad
      return {
        mensajes: [],
        threads: [],
        totalThreads: 0,
        totalMessages: 0,
        paginaActual: pagina,
        tamañoPagina: tamañoPagina,
        totalPaginas: 1,
        total: 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
      
    } catch (error) {
      console.error('❌ Error al obtener mensajes:', error);
      throw error;
    }
  },

  /**
   * Obtiene los mensajes de threads de un asistente desde la BD (sin OpenAI)
   * @param {string} idAsistente - ID del asistente
   * @param {string} filtroRol - Rol a filtrar ('user' para mensajes de alumnos)
   * @param {number} pagina - Página actual (default: 1)
   * @param {number} tamañoPagina - Threads por página (default: 50, max: 200)
   * @param {number} limiteMensajesPorThread - Mensajes por thread (default: 100, max: 500)
   * @returns {Promise} Promesa que resuelve con la respuesta de la API
   */
  async getThreadsMessagesFromDb(
    idAsistente,
    filtroRol = 'user',
    pagina = 1,
    tamañoPagina = 300,
    limiteMensajesPorThread = 100,
    filters = {}
  ) {
    try {
      const params = new URLSearchParams();
      params.append('IdAsistente', idAsistente);
      if (filtroRol) params.append('FiltroRol', filtroRol);
      params.append('Pagina', String(pagina));
      params.append('TamañoPagina', String(Math.min(tamañoPagina, 300)));
      params.append('LimiteMensajesPorThread', String(Math.min(limiteMensajesPorThread, 500)));
      if (filters.texto) params.append('Texto', filters.texto);
      if (filters.fechaDesde) params.append('FechaDesde', filters.fechaDesde);
      if (filters.fechaHasta) params.append('FechaHasta', filters.fechaHasta);

      const response = await apiService.get(`/Asistente/threads-messages-db?${params.toString()}`);

      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los mensajes');
      }

      if (response.threads && Array.isArray(response.threads)) {
        const mensajesAplanados = [];

        response.threads.forEach(thread => {
          if (thread.mensajes && Array.isArray(thread.mensajes)) {
            thread.mensajes.forEach(mensaje => {
              mensajesAplanados.push({
                ...mensaje,
                threadId: thread.idThread,
                fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
                fechaEnviado: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                fecha: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                mensaje: mensaje.contenido || '',
                usuario: mensaje.rol === 'user' ? 'Estudiante' : 'Asistente',
                rol: mensaje.rol
              });
            });
          }
        });

        return {
          mensajes: mensajesAplanados,
          threads: response.threads,
          totalThreads: response.totalThreads || 0,
          totalMessages: response.totalMessages || 0,
          diaMasActivo: response.diaMasActivo || response.DiaMasActivo || null,
          mensajesDiaMasActivo: response.mensajesDiaMasActivo ?? response.MensajesDiaMasActivo ?? 0,
          horaPico: response.horaPico ?? response.HoraPico ?? null,
          mensajesHoraPico: response.mensajesHoraPico ?? response.MensajesHoraPico ?? 0,
          promedioMensajesPorDiaActivo: response.promedioMensajesPorDiaActivo ?? response.PromedioMensajesPorDiaActivo ?? 0,
          diasConActividad: response.diasConActividad ?? response.DiasConActividad ?? 0,
          ultimaActividad: response.ultimaActividad || response.UltimaActividad || null,
          paginaActual: response.paginaActual || pagina,
          tamañoPagina: response.tamañoPagina || tamañoPagina,
          totalThreadsEnBD: response.totalThreadsEnBD || 0,
          totalPaginas: response.totalPaginas || 1,
          tienePaginaSiguiente: response.tienePaginaSiguiente || false,
          tienePaginaAnterior: response.tienePaginaAnterior || false,
          total: response.totalMessages || 0,
          informacionUtilRangoFechas: response.informacionUtilRangoFechas || response.InformacionUtilRangoFechas || '',
          informacionUtilError: response.informacionUtilError || response.InformacionUtilError || '',
          mensajesAnalizadosPorOpenAI: response.mensajesAnalizadosPorOpenAI ?? response.MensajesAnalizadosPorOpenAI ?? 0,
          ok: response.ok,
          statusCode: response.statusCode
        };
      }

      return {
        mensajes: [],
        threads: [],
        totalThreads: 0,
        totalMessages: 0,
        paginaActual: pagina,
        tamañoPagina: tamañoPagina,
        totalPaginas: 1,
        total: 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
    } catch (error) {
      console.error('❌ Error al obtener mensajes desde BD:', error);
      throw error;
    }
  },

  /**
   * Obtiene la conversación completa de un thread específico
   * @param {string} idAsistente - ID del asistente (formato OpenAI)
   * @param {string} idThread - ID del thread
   * @param {number} limiteMensajes - Límite de mensajes (default: 100)
   * @returns {Promise} Promesa que resuelve con la conversación completa
   */
  async getThreadConversation(idAsistente, idThread, limiteMensajes = 100, options = {}) {
    try {
      
      const { usarOpenAI = false, idColegio = null } = options || {};

      const requestBody = {
        idAsistente: idAsistente,
        idThread: idThread,
        limiteMensajes: limiteMensajes,
        usarOpenAI: Boolean(usarOpenAI),
        idColegio: idColegio
      };
      
      
      const response = await apiService.post('/Asistente/thread-conversation', requestBody);
      
      
      // Validar estructura de respuesta
      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener la conversación');
      }
      
      return {
        idThread: response.idThread,
        idAsistente: response.idAsistente,
        fechaCreacionThread: response.fechaCreacionThread,
        mensajes: response.mensajes || [],
        totalMensajes: response.totalMensajes || 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
      
    } catch (error) {
      console.error('❌ Error al obtener conversación:', error);
      throw error;
    }
  }
};

export default ThreadsService;
